/**
 * Where the slash-command menu goes on screen (FRONT-1355).
 *
 * The menu used to be an absolutely-positioned child of the composer, floating
 * "above" it. That only ever worked while no ancestor clipped its overflow —
 * and the chat modal's card sets `overflow-hidden`, so in a short window the
 * list was cut off at the card's top edge with the first commands hidden.
 *
 * It is now rendered into a portal at the document root and positioned against
 * the viewport, so no ancestor can clip it. This module owns that arithmetic
 * and nothing else: given the composer's box and the window height, it decides
 * which side to open on and how tall the menu may be. Pure, so the awkward
 * cases (a very short window, a composer hard against the top of the screen)
 * are unit-testable without a browser.
 */

/** Gap between the composer and the menu. */
export const SLASH_MENU_GAP = 8;
/** Smallest gap we leave between the menu and the edge of the window. */
export const SLASH_MENU_VIEWPORT_MARGIN = 12;
/** The menu never grows past this, however tall the window is. */
export const SLASH_MENU_MAX_HEIGHT = 360;
/**
 * The floor. When neither side of the composer has real room — a window only a
 * few hundred pixels tall — a menu clamped to the space available would come
 * out at zero height, i.e. invisible. Overlapping the composer a little is a
 * worse-but-honest outcome than the user pressing "/" and seeing nothing.
 */
export const SLASH_MENU_MIN_HEIGHT = 120;

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  width: number;
}

export interface SlashMenuPosition {
  placement: 'above' | 'below';
  left: number;
  width: number;
  /** Set when placement is 'below' — distance from the top of the window. */
  top?: number;
  /** Set when placement is 'above' — distance from the bottom of the window. */
  bottom?: number;
  /** Hard cap on the menu's height so it always fits on screen. */
  maxHeight: number;
}

/**
 * Prefer opening upwards — a composer sits at the bottom of its surface, and
 * the eye expects the list above the thing being typed into. Go downwards only
 * when the menu cannot fit above at full size AND there is genuinely more room
 * below: showing a cramped list above while half the window sits empty below
 * it is the same complaint this ticket is about, one step removed.
 */
export function slashMenuPosition(
  anchor: AnchorRect,
  viewportHeight: number,
): SlashMenuPosition {
  const roomAbove = anchor.top - SLASH_MENU_GAP - SLASH_MENU_VIEWPORT_MARGIN;
  const roomBelow =
    viewportHeight - anchor.bottom - SLASH_MENU_GAP - SLASH_MENU_VIEWPORT_MARGIN;
  const placeAbove = roomAbove >= SLASH_MENU_MAX_HEIGHT || roomAbove >= roomBelow;
  const room = Math.max(0, placeAbove ? roomAbove : roomBelow);
  const maxHeight = Math.max(
    SLASH_MENU_MIN_HEIGHT,
    Math.min(SLASH_MENU_MAX_HEIGHT, room),
  );
  return placeAbove
    ? {
        placement: 'above',
        left: anchor.left,
        width: anchor.width,
        bottom: viewportHeight - anchor.top + SLASH_MENU_GAP,
        maxHeight,
      }
    : {
        placement: 'below',
        left: anchor.left,
        width: anchor.width,
        top: anchor.bottom + SLASH_MENU_GAP,
        maxHeight,
      };
}
